import { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer'
import { recordAnnotationCreated } from 'features/kestrelViewer/metrics'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { getBatchTypeConfig } from 'features/kestrelViewer/utils/surfaceAreaBatchConfig'
import {
  KestrelInspectionType,
  KVAerospaceAnnotation,
  KVAnnotation,
  KVG1N6Annotation,
  KVNavyAnnotation,
  KVPolygonGeometry,
  RolodexEntryPatchJson,
  WorldPosition,
} from 'types'

import { KVMessageDef } from './index'

export function getNewPolygonAnnotationsBatchMessage() {
  return {
    type: 'newSurfaceAreasBatch',
    requiredData: ['items'],

    callback: (
      json: Record<string, unknown>,
      kv: KestrelViewerMessageCallbackAPI
    ) => {
      const items = (json.items as Array<Record<string, unknown>>) ?? []
      if (!Array.isArray(items) || items.length === 0) {
        return
      }

      // Read batchType from JSON
      const batchType = (json.batchType as string) || undefined
      const batchConfig = getBatchTypeConfig(batchType)

      // Notify if this is a textSegmentation batch
      if (batchType === 'textSegmentation' && kv.onTextSegmentationComplete) {
        kv.onTextSegmentationComplete()
      }

      // Prepare a single rolodex patch after all items are added
      const userName = kv.user?.name ?? ''
      const userEmail = kv.user?.email ?? ''

      const newAnnotations: KVAnnotation[] = []
      let counter = 0
      for (const item of items) {
        counter = counter + 1

        const id = item.surfaceAreaId as string
        if (!id) continue
        const annotation = new KVAnnotation(id)

        // Prefer surfaceAreaLabel; fallback to polygonLabel; fallback default
        // Check for non-empty strings since empty strings are sent by default
        const surfaceLabel = (item.surfaceAreaLabel as string) || ''
        const polygonLabel = (item.polygonLabel as string) || ''
        const label =
          (surfaceLabel && surfaceLabel.trim()) ||
          (polygonLabel && polygonLabel.trim()) ||
          batchConfig.defaultLabelPrefix + counter
        annotation.label = label

        // Apply tag based on batch type
        // Use provided tagValue if available, otherwise default from batch config
        const tagValue =
          (item.tagValue as string | undefined) ||
          (json.tagValue as string | undefined) ||
          batchConfig.tagValue

        annotation.tags[batchConfig.tagKey] = {
          value: tagValue,
          derivedTag: true,
        }

        annotation.inspectionArea =
          (item.inspectionArea as string | null) ?? null

        if (item.creationRayDirection) {
          annotation.creationRayDirection =
            item.creationRayDirection as WorldPosition
        }
        if (item.cameraFacingNormal) {
          annotation.cameraFacingNormal =
            item.cameraFacingNormal as WorldPosition
        }

        // Keep tag for backward compatibility
        if (annotation.inspectionArea) {
          annotation.tags['inspectionArea'] = {
            value: annotation.inspectionArea,
            derivedTag: true,
          }
        }

        annotation.humanValidated = (item.humanValidated as boolean) ?? false

        annotation.visible = (item.surfaceAreaVisible as boolean) ?? true

        annotation.userName = userName
        annotation.userEmail = userEmail

        annotation.severity = (item.surfaceAreaSeverity as string) ?? '3'

        annotation.isSegmentationSurfaceArea =
          (item.isSegmentationSurfaceArea as boolean) ?? false

        // Area and perimeter may arrive as strings; coerce to number if present
        if (item.area !== null) {
          annotation.area = parseFloat(item.area as string)
        }
        if (item.perimeter !== null) {
          annotation.perimeter = parseFloat(item.perimeter as string)
        }

        // Bounding box dimensions may arrive as strings; coerce to number if present
        if (
          item.boundingBoxLength !== null &&
          item.boundingBoxLength !== undefined
        ) {
          annotation.boundingBoxLength = parseFloat(
            item.boundingBoxLength as string
          )
        }
        if (
          item.boundingBoxWidth !== null &&
          item.boundingBoxWidth !== undefined
        ) {
          annotation.boundingBoxWidth = parseFloat(
            item.boundingBoxWidth as string
          )
        }

        // Geometry
        const polygonGeometry = new KVPolygonGeometry(
          item.surfaceAreaPoints as WorldPosition[],
          item.normal as WorldPosition
        )
        annotation.geometry = polygonGeometry

        // Centered info for zooming/photo selection
        annotation.centeredNormal = item.normal as WorldPosition
        annotation.centeredPosition = item.centeredPosition as WorldPosition

        const inspectionType = kv.kestrelModel?.data.inspection_type

        // Navy optional
        if (inspectionType === KestrelInspectionType.NAVY) {
          annotation.navyAnnotation = new KVNavyAnnotation()
        }
        // G1N6 optional
        if (inspectionType === KestrelInspectionType.G1N6) {
          annotation.g1n6Annotation = new KVG1N6Annotation()
        }
        // Aerospace optional
        if (inspectionType === KestrelInspectionType.AEROSPACE) {
          annotation.aerospaceAnnotation = new KVAerospaceAnnotation()
        }

        // Apply whiteboard tag immediately if in SystemView context
        if (kv.selectedWhiteboardId) {
          annotation.tags = {
            ...annotation.tags,
            whiteboard: {
              derivedTag: false,
              value: kv.selectedWhiteboardId,
            },
          }
        }

        // Add to new annotations array
        newAnnotations.push(annotation)
      }

      // Create new array with existing + new annotations to trigger state update
      const updatedAnnotations = kv.annotations
        ? [...kv.annotations, ...newAnnotations]
        : newAnnotations
      kv.setAnnotations(updatedAnnotations)

      // Track annotation creation for session metrics (count each in batch)
      for (let i = 0; i < newAnnotations.length; i++) {
        recordAnnotationCreated()
      }

      // Single rolodex patch for the whole batch
      const patchData: RolodexEntryPatchJson = {
        id: kv.kestrelModel?.id ?? '',
        data: {
          annotations: updatedAnnotations.map((a) => KVAnnotation.toJSON(a)),
        },
      }
      kv.rolodexPatch([patchData])

      // Toggle measurements off
      if (kv.pixelStream) {
        kv.pixelStream.emitUIInteraction({
          command: KestrelCommands.SET_MEASUREMENT_LABEL_VISIBILITY,
          labelsVisible: false,
        })

        // Send label updates in chunks across frames to avoid flooding network
        // UPDATE_SURFACE_AREA only supports one-at-a-time updates
        const UPDATE_BATCH_SIZE = 30 // Match annotation loading batch size

        let chunkIndex = 0
        const processChunk = () => {
          if (!kv.pixelStream) return

          const pixelStream = kv.pixelStream // Capture for TypeScript

          const startIdx = chunkIndex * UPDATE_BATCH_SIZE
          const endIdx = Math.min(
            startIdx + UPDATE_BATCH_SIZE,
            newAnnotations.length
          )
          const chunk = newAnnotations.slice(startIdx, endIdx)

          chunk.forEach((annotation) => {
            pixelStream.emitUIInteraction({
              command: KestrelCommands.UPDATE_SURFACE_AREA,
              surfaceAreaId: annotation.id,
              surfaceAreaLabel: annotation.label,
            })
          })

          if (endIdx < newAnnotations.length) {
            chunkIndex++
            requestAnimationFrame(processChunk)
          } else {
            // All chunks processed, make labels visible
            pixelStream.emitUIInteraction({
              command: KestrelCommands.SET_LABEL_VISIBILITY,
              labelsVisible: true,
            })
          }
        }

        if (newAnnotations.length > 0) {
          requestAnimationFrame(processChunk)
        }
      }
    },
  } as KVMessageDef
}
